import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Star, Phone as PhoneIcon, Calendar, Eye, Gift, Users, Trophy } from "lucide-react";

const BENEFITS = [
  { icon: Eye, title: "Priority Access", text: "First look at limited-availability expeditions before they're listed publicly. When a top-tier hunt opens up, you hear about it first." },
  { icon: PhoneIcon, title: "Direct Line to Your Advisor", text: "Skip the application. Call your dedicated advisor directly anytime to discuss opportunities, ask questions, or start planning your next trip." },
  { icon: Calendar, title: "Annual Invitational", text: "An exclusive gathering at a premier lodge destination. Small group. Top hunters. Sporting activities, whisky, cigars, and the kind of conversations that only happen in the field." },
  { icon: Star, title: "First-Look Opportunities", text: "New outfitter partnerships, special season openings, and cancellation spots come to Circle members before anyone else." },
  { icon: Gift, title: "Gear & Brand Perks", text: "Access to partner brand deals, custom gear collaborations, and equipment recommendations from operators who actually use the stuff." },
  { icon: Users, title: "Referral Recognition", text: "When you refer a hunter who books, we take care of you. Credits, upgrades, or priority scheduling — your loyalty is noticed." },
  { icon: Trophy, title: "Top Priority Planning", text: "Your trips get planned first. Complex international logistics, multi-leg expeditions, back-to-back hunts — Circle members get the most attention." },
];

export default function DispatchCircle() {
  return (
    <>
      <Helmet>
        <title>The Dispatch Circle | Dispatch Hunts</title>
        <meta name="description" content="The Dispatch Circle is our private advisory tier for hunters who book $25,000+ per year. Priority access. Direct line. Annual gathering." />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center" data-testid="circle-hero">
        <div className="absolute inset-0">
          <img src="https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Spring-Alaska1.jpg" alt="Hunting expedition" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-carbon/85" />
        </div>
        <div className="container-dh relative z-10 pt-32 pb-16 text-center max-w-3xl mx-auto">
          <p className="text-label text-brass mb-4">For Serious Hunters</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-bone mb-6" data-testid="circle-headline">
            The Dispatch Circle
          </h1>
          <p className="font-body text-bone/60 text-lg max-w-2xl mx-auto">
            Not a loyalty program. Not a points system. This is how we take care of the hunters who are as serious about this as we are.
          </p>
        </div>
      </section>

      {/* What Is It */}
      <section className="section-light py-20 lg:py-28" data-testid="circle-what">
        <div className="container-dh max-w-3xl">
          <div className="text-center mb-16">
            <p className="text-label text-brass mb-3">Earned, Not Bought</p>
            <h2 className="font-display text-3xl font-bold text-carbon mb-6">
              The Circle is for hunters who book $25,000 or more in a calendar year.
            </h2>
            <p className="font-body text-slate leading-relaxed max-w-2xl mx-auto">
              When you hit that threshold, you're automatically in. No application. No fee. It's our way of recognizing the hunters who trust us with their most important trips — and making sure they get the best of what we have to offer.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="circle-benefits">
        <div className="container-dh">
          <p className="text-label text-brass mb-3">Circle Benefits</p>
          <h2 className="font-display text-3xl font-bold text-bone mb-14">What you get.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BENEFITS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="border border-bone/10 p-6" data-testid={`benefit-${title.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon className="w-6 h-6 text-brass mb-4" />
                <h3 className="font-display text-lg font-bold text-bone mb-2">{title}</h3>
                <p className="font-body text-sm text-bone/50 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Annual Gathering */}
      <section className="section-chalk py-20 lg:py-28" data-testid="circle-gathering">
        <div className="container-dh">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-label text-brass mb-3">The Invitational</p>
              <h2 className="font-display text-3xl font-bold text-carbon mb-6">
                Once a year. The right people. The right place.
              </h2>
              <div className="space-y-4 font-body text-slate leading-relaxed">
                <p>
                  The Dispatch Circle Invitational is an annual gathering at a premier lodge destination. Twelve to fifteen hunters. Three days. No agenda except good company and the kind of experiences that don't happen at a conference.
                </p>
                <p>
                  Think sporting clays at dawn. Fly fishing in the afternoon. Whisky and cigars by the fire. Conversations with hunters who've been where you want to go — and operators who can get you there.
                </p>
                <p>
                  This isn't a sales event. It's not a networking mixer. It's an insider gathering for people who take hunting seriously and want to be around others who do the same.
                </p>
              </div>
            </div>
            <div className="relative">
              <img src="https://dispatchhunts.com/wp-content/uploads/2024/12/Moose-Fall-Alaska-4.jpg" alt="Lodge setting" className="w-full aspect-[4/3] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* How to Qualify */}
      <section className="section-light py-20 lg:py-28" data-testid="circle-qualify">
        <div className="container-dh max-w-2xl text-center">
          <p className="text-label text-brass mb-3">Qualification</p>
          <h2 className="font-display text-3xl font-bold text-carbon mb-8">How to get in.</h2>
          <div className="bg-chalk p-8 sm:p-10 border border-carbon/5">
            <p className="font-display text-5xl font-bold text-brass mb-2">$25,000+</p>
            <p className="font-body text-slate mb-6">in booked expeditions within a calendar year</p>
            <div className="space-y-3 text-left max-w-md mx-auto font-body text-sm text-slate">
              <p>Book one $25K+ hunt or combine multiple trips. Once you hit the threshold, we'll reach out personally to welcome you into the Circle.</p>
              <p>Existing members can also refer new hunters directly into the program.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark grain py-20" data-testid="circle-cta">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl font-bold text-bone mb-4">Start with your next hunt.</h2>
          <p className="font-body text-bone/50 mb-8">The Circle begins with a conversation. Apply for a consultation and let's find the right expedition.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/apply" className="btn-brass" data-testid="circle-apply-btn">Apply for a Consultation <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/login" className="btn-outline" data-testid="circle-member-login">Member Sign In <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <p className="font-body text-bone/30 text-sm mt-4">Already a Circle member? <Link to="/login" className="text-brass hover:underline">Sign in to your dashboard</Link></p>
        </div>
      </section>
    </>
  );
}
